import { CalendarOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { DatePicker } from 'antd';
import type { Dayjs } from 'dayjs';
import { useRef, useState } from 'react';
import styled from 'styled-components';

import { Flex } from '@/components/base';
import Button from '@/components/Button/Button';
import {
  FilterItemStyle,
  FilterKeyStyle,
} from '@/components/Filter/FilterItem/FilterItem';
import Portal from '@/components/Portal/Portal';
import Typo from '@/components/Typography/Typo';
import { RangeUnit, rangeUnitMap } from '@/utils/getDatePreset';
import { DATE_FORMAT, getTimezoneDayjs } from '@/utils/time';

type Props = {
  date: Dayjs | null;
  onChange: (date: Dayjs | null) => void;
};

const EntireDateSelector = (props: Props) => {
  const [open, setOpen] = useState(false);

  if (!props.date)
    return (
      <Button
        sizeType="m"
        variant="secondary"
        style={{ width: 'fit-content' }}
        rounded
        leftIcon={<CalendarOutlined />}
        onClick={() => {
          props.onChange(getTimezoneDayjs());
          setOpen(true);
        }}
      >
        Entire Period
      </Button>
    );

  return (
    <FilterItemStyle>
      <Flex>
        <CustomDatePicker
          open={open}
          setOpen={setOpen}
          date={props.date}
          onChange={props.onChange}
        />
        <FilterKeyStyle
          onClick={(e) => {
            e.stopPropagation();
            props.onChange(null);
          }}
        >
          <CloseCircleOutlined style={{ fontSize: 14 }} />
        </FilterKeyStyle>
      </Flex>
    </FilterItemStyle>
  );
};

type DatePickerProps = {
  date: Dayjs;
  onChange: (value: Dayjs) => void;
  setOpen: (open: boolean) => void;
  open: boolean;
};

const CustomDatePicker = (props: DatePickerProps) => {
  const ref = useRef<HTMLDivElement>(null);
  return (
    <FilterKeyStyle a="center" style={{ paddingRight: 8 }} ref={ref}>
      <DatePicker
        open={props.open}
        style={{ opacity: 0, width: 0, padding: 0, zIndex: 100 }}
        value={props.date}
        onChange={(date) => {
          if (!date) return;
          props.onChange(
            getTimezoneDayjs(date.startOf(rangeUnitMap[RangeUnit.Daily])),
          );
          props.setOpen(false);
        }}
        getPopupContainer={() => {
          const layer = document.getElementById('dropdown-layer');
          return layer as HTMLDivElement;
        }}
      />
      <Typo type="label2" onClick={() => props.setOpen(true)}>
        {props.date.format(DATE_FORMAT.base)}
      </Typo>
      {props.open && (
        <Portal id="dropdown-layer">
          <Dimmer onClick={() => props.setOpen(false)} />
        </Portal>
      )}
    </FilterKeyStyle>
  );
};

const Dimmer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 50;
`;

export default EntireDateSelector;
